import React, { useState, useEffect } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { optimizeCloudinaryUrl, getBlurDataUrl, generateSrcSet, getImageSizes } from '../utils/imageOptimization';

/**
 * Optimized Lazy Loading Image Component
 * Features:
 * - Lazy loading with intersection observer
 * - Blur placeholder effect
 * - Cloudinary automatic optimization
 * - Responsive images with srcSet
 * - WebP/AVIF format support
 */
const LazyImage = ({
  src,
  alt,
  className = '',
  type = 'card', // 'hero', 'card', 'detail', 'thumbnail'
  width,
  height,
  aspectRatio,
  onLoad,
  onError,
  wrapperClassName = '',
  eager = false, // NEW: Force eager loading for above-the-fold images
  priority = false, // NEW: High priority for critical images
  ...props
}) => {
  const [hasError, setHasError] = useState(false);

  // Ensure src is a string
  const safeSrc = React.useMemo(() => {
    if (!src) return '';
    if (typeof src === 'string') return src;
    if (Array.isArray(src) && src.length > 0 && typeof src[0] === 'string') return src[0];
    return '';
  }, [src]);

  // Optimize image URL for Cloudinary
  const optimizedSrc = optimizeCloudinaryUrl(safeSrc, {
    width: width || (type === 'hero' ? 1920 : type === 'card' ? 800 : 1200),
    quality: eager ? 'auto:best' : 'auto:good', // Better quality for eager loaded images
    format: 'auto',
  });

  // Generate blur placeholder (only for lazy loaded images)
  const placeholderSrc = eager ? undefined : getBlurDataUrl(safeSrc);

  // Generate responsive srcSet
  const srcSet = generateSrcSet(safeSrc);

  // Get sizes attribute
  const sizes = getImageSizes(type);

  // Fallback image
  const fallbackImage = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"%3E%3Crect fill="%23f0f0f0" width="400" height="300"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%23999" font-family="Arial" font-size="16"%3EGörsel Yüklenemedi%3C/text%3E%3C/svg%3E';

  const handleError = (e) => {
    setHasError(true);
    if (onError) onError(e);
  };

  const handleLoad = (e) => {
    if (onLoad) onLoad(e);
  };

  // Container style for aspect ratio
  const containerStyle = aspectRatio
    ? { paddingBottom: `${(1 / aspectRatio) * 100}%` }
    : {};

  // If eager loading is requested, render regular img tag
  if (eager) {
    return (
      <div 
        className={`relative overflow-hidden ${wrapperClassName}`}
        style={containerStyle}
      >
        <img
          src={hasError ? fallbackImage : optimizedSrc}
          alt={alt}
          className={className}
          width={width}
          height={height}
          srcSet={!hasError ? srcSet : undefined}
          sizes={!hasError ? sizes : undefined}
          onLoad={handleLoad}
          onError={handleError}
          loading="eager"
          decoding="async"
          fetchPriority={priority ? "high" : "auto"}
          {...props}
        />
      </div>
    );
  }

  // Lazy loading for below-the-fold images
  return (
    <div 
      className={`relative overflow-hidden ${wrapperClassName}`}
      style={containerStyle}
    >
      <LazyLoadImage
        src={hasError ? fallbackImage : optimizedSrc}
        alt={alt}
        className={className}
        placeholderSrc={placeholderSrc}
        effect="blur"
        threshold={200} // Increased from 100px to 200px for earlier loading
        useIntersectionObserver={true}
        width={width}
        height={height}
        srcSet={!hasError ? srcSet : undefined}
        sizes={!hasError ? sizes : undefined}
        onLoad={handleLoad}
        onError={handleError}
        loading="lazy"
        decoding="async"
        fetchPriority="low" // Lower priority for lazy loaded images
        {...props}
      />
    </div>
  );
};

export default LazyImage;
